import { QueryClient, useQuery } from "@tanstack/react-query";
import { SentryFeatureTags, logException } from "@punchup/sentry";
import { VenuePageNavItemRow, getVenuePageNavItems } from "../../data";

import { Database } from "@punchup/data/supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { createClientComponentClient } from "@punchup/data/supabase/client";

export function useVenuePageNavItems(venuePageId: string): {
  navItems: VenuePageNavItemRow[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const queryFn = async (): Promise<VenuePageNavItemRow[]> => {
    const supabase = createClientComponentClient<Database>();

    try {
      const navItems = await getVenuePageNavItems(supabase, venuePageId);
      return navItems;
    } catch (error) {
      logException(error as Error, {
        extra: {
          data: `error fetching nav items for venue page: ${venuePageId}`,
        },
        tags: {
          feature: SentryFeatureTags.VenuePages,
          supabase: true,
        },
      });
      throw error;
    }
  };

  const {
    data: navItems = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["venue-page-nav-items", venuePageId],
    queryFn,
    staleTime: 5 * 60 * 1000,
  });

  return { navItems, isLoading, error, refetch };
}

export async function prefetchVenuePageNavItems(
  queryClient: QueryClient,
  supabase: SupabaseClient<Database>,
  venuePageId: string
) {
  await queryClient.prefetchQuery({
    queryKey: ["venue-page-nav-items", venuePageId],
    queryFn: async () => {
      return await getVenuePageNavItems(supabase, venuePageId);
    },
    staleTime: 5 * 60 * 1000,
  });
}
